import axios from 'axios';
import BASE_URL from '@rti/shared/src/services/baseUrl';
import { apiClient } from '@rti/shared';

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface ChemOrbisDataPoint {
  date: string;
  week_number: number;
  ethylene_cfr: number | null;
  ethylene_fd: number | null;
  propylene_cfr: number | null;
  propylene_fd: number | null;
  styrene_fob_nwe: number | null;
  styrene_fob_usg: number | null;
  styrene_cfr: number | null;
  vcm_fob: number | null;
  benzene_cif: number | null;
  acn_cfr: number | null;
  meg_cfr: number | null;
  pta_cfr: number | null;
  paraxylene_cfr: number | null;
}

export interface PetchemDataPoint {
  date: string;
  wti_crude_oil: number | null;
  brent_crude_oil: number | null;
  spot_ethylene: number | null;
  nat_gas: number | null;
  ethane: number | null;
  propane: number | null;
  spot_pgp: number | null;
  spot_rgp: number | null;
  spot_benzene: number | null;
  spot_styrene: number | null;
  spot_butadiene: number | null;
  [key: string]: any;
}

let petchemCache: PetchemDataPoint[] | null = null;
let chemOrbisCache: ChemOrbisDataPoint[] | null = null;

export const getPetchemData = async (): Promise<PetchemDataPoint[]> => {
  if (petchemCache) return petchemCache;
  try {
    const cached = await apiClient.fetchCachedPetchemData();
    if (cached) {
      petchemCache = Array.isArray(cached) ? cached as PetchemDataPoint[] : (cached as any).data || [];
      return petchemCache!;
    }
  } catch { /* fall through to live fetch */ }
  const response = await axios.get(
    `${BASE_URL}/gateway/synapse/d/rti-petchem`,
    { headers: getAuthHeaders() }
  );
  petchemCache = Array.isArray(response.data) ? response.data : [];
  return petchemCache!;
};

export const getChemOrbisData = async (): Promise<ChemOrbisDataPoint[]> => {
  if (chemOrbisCache) return chemOrbisCache;
  try {
    const cached = await apiClient.fetchCachedChemOrbisData();
    if (cached) {
      chemOrbisCache = Array.isArray(cached) ? cached as ChemOrbisDataPoint[] : (cached as any).data || [];
      return chemOrbisCache!;
    }
  } catch { /* fall through to live fetch */ }
  const response = await axios.get(
    `${BASE_URL}/gateway/synapse/w/orbis-price-wizard`,
    { headers: getAuthHeaders() }
  );
  chemOrbisCache = Array.isArray(response.data) ? response.data : [];
  return chemOrbisCache!;
};

export const clearFeedstockCache = () => {
  petchemCache = null;
  chemOrbisCache = null;
};

export const computeMovingAverage = (
  values: (number | null)[],
  period: number
): (number | null)[] => {
  return values.map((_, i) => {
    if (i < period - 1) return null;
    let sum = 0;
    let count = 0;
    for (let j = i - period + 1; j <= i; j++) {
      if (values[j] !== null && values[j] !== undefined) {
        sum += values[j]!;
        count++;
      }
    }
    return count > 0 ? sum / count : null;
  });
};

export const getLatestMA = (
  values: (number | null)[],
  period: number
): number | null => {
  const ma = computeMovingAverage(values, period);
  for (let i = ma.length - 1; i >= 0; i--) {
    if (ma[i] !== null) return ma[i];
  }
  return null;
};

export const getLatestValue = (values: (number | null)[]): number | null => {
  for (let i = values.length - 1; i >= 0; i--) {
    if (values[i] !== null) return values[i];
  }
  return null;
};
